import React, { useEffect, useMemo, useState } from 'react';
import { COUNTRIES, flagOf, countryByIso, labelOf, splitPhone, toE164 } from '@/lib/countries';

/**
 * Phone input with the country code attached rather than typed.
 *
 * A lead who enters "9876543210" and one who enters "+91 98765 43210" are the
 * same person, but only the second can be WhatsApped, matched to a CAPI event,
 * or deduped against an existing record. So the country is picked, never typed,
 * and what leaves the form is always E.164.
 *
 * The picker is a native <select> laid transparently over a small chip: the chip
 * stays narrow (flag + dial code) while the dropdown still shows full country
 * names, and on mobile it is the OS wheel rather than a list we would have to
 * build and get wrong.
 */
export function PhoneField({
  id = 'phone',
  value,
  iso,
  onChange,          // (e164, iso) => void
  placeholder,
  required,
  onBlur,
  autoComplete = 'tel',
}) {
  // The country has to live here, not be derived from the value alone. Deriving
  // it works the moment there are digits, but picking a country BEFORE typing
  // produced an empty E.164 string, which encodes no country, so the choice was
  // immediately forgotten and the chip snapped back.
  const [pickedIso, setPickedIso] = useState(() => splitPhone(value, iso).iso);
  const country = countryByIso(pickedIso) || countryByIso(iso) || countryByIso('IN');

  // A pasted number that carries its own code still wins.
  useEffect(() => {
    const fromValue = splitPhone(value, country.iso).iso;
    if (fromValue && fromValue !== country.iso) setPickedIso(fromValue);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // So does answering the form's country question, while the box is still empty.
  useEffect(() => {
    if (iso && iso !== country.iso && !String(value || '').replace(/\D/g, '')) setPickedIso(iso);
  }, [iso]); // eslint-disable-line react-hooks/exhaustive-deps
  // The digits the visitor sees, without the country code - showing "+91" twice
  // (once in the chip, once in the box) is what makes people delete one of them.
  const national = useMemo(() => splitPhone(value, country.iso).national, [value, country.iso]);

  const setCountry = (nextIso) => {
    setPickedIso(nextIso);
    onChange(toE164(nextIso, national), nextIso);
  };
  const setNumber = (raw) => {
    // A pasted "+44 7700 900000" or "919876543210" carries its own country: honour
    // it instead of prefixing on top of it.
    const parsed = splitPhone(raw, country.iso);
    setPickedIso(parsed.iso);
    onChange(toE164(parsed.iso, parsed.national), parsed.iso);
  };

  return (
    <div className="flex items-stretch gap-2">
      <div className="relative shrink-0">
        <div
          className="flex h-10 items-center gap-1 rounded-md border border-input bg-field text-field-foreground pl-2.5 pr-6 text-sm shadow-sm"
          aria-hidden="true"
        >
          <span className="text-base leading-none">{flagOf(country.iso)}</span>
          <span className="tabular-nums text-muted-foreground">+{country.dial}</span>
        </div>
        <svg className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <select
          aria-label="Country calling code"
          value={country.iso}
          onChange={(e) => setCountry(e.target.value)}
          className="absolute inset-0 size-full cursor-pointer opacity-0"
        >
          {COUNTRIES.map((c) => (
            <option key={c.iso} value={c.iso}>{labelOf(c)}</option>
          ))}
        </select>
      </div>

      <input
        id={id}
        type="tel"
        inputMode="tel"
        value={national}
        onChange={(e) => setNumber(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder || '98765 43210'}
        required={required}
        autoComplete={autoComplete}
        className="flex h-10 w-full rounded-md border border-input bg-field text-field-foreground px-3 text-sm shadow-sm placeholder:text-field-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      />
    </div>
  );
}
